import 'url-search-params-polyfill';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import {
  getWrapperInterface,
  isElectronApp,
  getLatestElectronWrapperVersion,
  isAppleCPU,
  getValueFromLocalStorage,
  setValueInLocalStorage,
  postMessageToIosWrapper
} from './utils';
require('keyboardevent-key-polyfill').polyfill();
const metadata = require('./metadata.json');
import mixpanel from 'mixpanel-browser';

delete (Array.prototype as any).group; // Fixes Safari 16.4+ issue: https://github.com/pixijs/pixijs/issues/9414

mixpanel.init('59d530cf4b7b071a80329fc0a35afcde', {
  api_host: 'https://api-eu.mixpanel.com',
  ignore_dnt: true,
  debug: true
});

if (isElectronApp()) {
  const wi = getWrapperInterface();
  if (wi) {
    if (wi.setWebAppBuild) {
      wi.setWebAppBuild(metadata.build);
    } else {
      wi.webAppBuild = metadata.build;
    }
  }
}

console.log(
  ' \n%cBeatflyer v.' + metadata.build + ' - ' + process.env.NODE_ENV + '%c\n ',
  'background: #eb3197; color: white; padding: 10px;',
  'background:transparent; padding:0'
);

import 'react-perfect-scrollbar/dist/css/styles.css';
import './scss/main.css';
import App from './App';
import AppVersion, { getWrapperVersion } from './data/AppVersion';
import { storeDispatch } from './state/Store';
import * as Actions from './state/actions';
import { REFERRER_STORAGE_KEY } from './data/Config';
import * as moment from 'moment';
import { reportBFError, trackEvent } from './utils/Tracker';
import { PlatformType } from 'data/PlatformType';
import { FlatStorableMotionData, LayerData } from 'data/FlyerData';
import { FigmaPaymentStatus } from 'data/Figma';
import { sendMeEmail } from 'utils/FirebaseUtils';

moment.updateLocale('en', {
  relativeTime: {
    future: 'in %s',
    past: '%s ago',
    s: '%ds',
    ss: '%ds',
    m: '%dm',
    mm: '%dm',
    h: '%dh',
    hh: '%dh',
    d: '%dd',
    dd: '%dd',
    M: '%dmo',
    MM: '%dmo',
    y: '%dy',
    yy: '%dy'
  }
});

const loadTheme = (cssFileUrl: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    var link = document.createElement('link');
    link.onload = () => resolve();
    link.onerror = reject;
    link.href = cssFileUrl;
    link.type = 'text/css';
    link.rel = 'stylesheet';
    link.media = 'screen,print';
    document.getElementsByTagName('head')[0].appendChild(link);
  });
};

const img = new Image();
img.onload = async () => {
  (window as any).BeatFlyer.noiseImage = img;
  const theme = (window as any).beatflyerTheme;
  if (theme && theme !== 'web' && theme !== 'desktop') {
    try {
      await loadTheme(`themes/${theme}.css`);
    } catch {
      console.error(`Could not load Theme "${theme}"`);
    }
  }
  postMessageToIosWrapper('appDidLoad');
  ReactDOM.render(<App />, document.getElementById('root'));
};
img.src = '/media/noise.png';

if (module.hot) {
  module.hot.accept('./showcase', () => {
    ReactDOM.render(<App />, document.getElementById('root'));
  });
}

const userDidInteract = () => {
  storeDispatch(Actions.userDidInteract());
  document.removeEventListener('mousedown', userDidInteract);
  document.removeEventListener('touchstart', userDidInteract);
};

document.addEventListener('mousedown', userDidInteract);
document.addEventListener('touchstart', userDidInteract);

const WRAPPER_VERSION_CHECK_STORAGE_KEY = 'WRAPPER_VERSION_CHECK_STORAGE_KEY';

if (
  isElectronApp() &&
  localStorage.getItem(WRAPPER_VERSION_CHECK_STORAGE_KEY) !== 'false'
) {
  console.log('CHECK BUILD VERSION...');
  // check if newer version of the wrapper is available
  const current = getWrapperVersion();
  getLatestElectronWrapperVersion()
    .then((latest: AppVersion) => {
      if (latest.isNewerThan(current)) {
        // update is available
        const dialog = (window as any).electron.remote.dialog;
        const buttons = ['Download', 'Not now'];
        if (!isAppleCPU()) {
          buttons.push(`Don't check again`);
        }
        return dialog.showMessageBox({
          type: 'info',
          title: 'Update your app',
          message:
            'A new version of Beatflyer is now available. Please update to access the latest fixes and features.',
          buttons
        });
      } else {
        return null;
      }
    })
    .then((response: any | null) => {
      if (response) {
        if (response.response === 0) {
          window.location.href = 'https://beatflyer.com/download';
        } else if (response.response === 2) {
          // don't check again
          setValueInLocalStorage(WRAPPER_VERSION_CHECK_STORAGE_KEY, 'false');
        }
      }
    })
    .catch((err: Error) => {
      reportBFError(err);
    });
} else {
  const urlVars: any = {};
  window.location.href.replace(
    /[?&]+([^=&]+)=([^&]*)/gi,
    (m: string, ...args: any[]): string => {
      const key = args[0];
      const value = args[1];
      urlVars[key] = value;
      return '';
    }
  );

  let referrer = getValueFromLocalStorage(REFERRER_STORAGE_KEY);
  if (!referrer) {
    // referrer = document.referrer;
    if (urlVars.ref) {
      referrer = urlVars.ref;
    }
    if (!referrer) {
      referrer = 'direct';
    }
    setValueInLocalStorage(REFERRER_STORAGE_KEY, referrer);
  }
}

window.onmessage = (msg: any) => {
  if (msg.data && msg.data.type) {
    switch (msg.data.type) {
      case 'BeatFlyerLayers': {
        // msg.data.paymentStatus = 'PAID'; // test
        const paymentStatus = msg.data.paymentStatus as
          | FigmaPaymentStatus
          | undefined;
        if (paymentStatus !== undefined) {
          storeDispatch(Actions.updateFigmaPaymentStatus(paymentStatus));
        }
        const layers = msg.data.layers as Uint8Array[] | string[]; // base64 string from sketch
        const width = msg.data.width as number;
        const height = msg.data.height as number;
        const showIntro = msg.data.showIntro as boolean;
        const bgColor = msg.data.bgColor as string | undefined;
        const layersData = msg.data.layersData as LayerData[] | undefined;
        const motionData = msg.data.motionData as
          | FlatStorableMotionData
          | undefined;
        storeDispatch(
          Actions.loadFigmaLayers(
            width,
            height,
            layers,
            showIntro,
            bgColor,
            layersData,
            motionData
          )
        );
        break;
      }
      case 'BeatFlyerPaymentStatus': {
        const paymentStatus = msg.data.status as FigmaPaymentStatus;
        if (paymentStatus === 'PAID') {
          trackEvent('Figma sale');
          void sendMeEmail('Figma sale', `🎉`);
        } else if (paymentStatus === 'NOT_SUPPORTED') {
          alert(
            'Payment feature is currently not supported, please try again later.'
          );
        }
        storeDispatch(Actions.updateFigmaPaymentStatus(paymentStatus));
        break;
      }
      default:
        break;
    }
  }
};
/*
// is there a way to test if it's loaded in Figma? Maybe if it's in iframe?
if (process.env.NODE_ENV === 'development' && (window as any).isBeatflyerLite) {
  storeDispatch(Actions.updateFigmaPaymentStatus('PAID'));
  storeDispatch(
    Actions.loadFigmaLayers(
      540,
      540,
      [
        'iVBORw0KGgoAAAANSUhEUgAAAhwAAAIcAQMAAACHMOU/AAAAA1BMVEUAAACnej3aAAAAO0lEQVR42u3BMQEAAADCIPuntsUuYAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAKEDkYwAAQucLdkAAAAASUVORK5CYII='
      ],
      true,
      '#ffffff'
    )
  );
}
*/

const urlParams = new URLSearchParams(window.location.search);
const templateIdForNewFlyer = urlParams.get('newFromTemplate');
if (templateIdForNewFlyer) {
  storeDispatch(Actions.createFlyerFromTemplateId(templateIdForNewFlyer));
  window.history.replaceState('', '', '/');
}

const base64DataForNewFlyer = urlParams.get('newFromData');
if (base64DataForNewFlyer) {
  const data = JSON.parse(atob(base64DataForNewFlyer));
  storeDispatch(Actions.createFlyerFromData(data));
  window.history.replaceState('', '', '/');
}

if ((window as any).beatflyerPlatform === PlatformType.Sketch) {
  // override window functions
  window.alert = msg => {
    window.top?.postMessage(
      {
        type: 'alert',
        msg
      },
      '*'
    );
  };
}
/*

Detect disconnection and send event to Electron. In theory Electron should swap vebview without losing the state of this webview.

const electron = (window as any).electron;
if (electron) {
  const ipcRenderer = electron.ipcRenderer;
  const updateOnlineStatus = () => {
    ipcRenderer.send(
      'online-status-changed',
      navigator.onLine ? 'online' : 'offline'
    );
  };

  window.addEventListener('online', updateOnlineStatus);
  window.addEventListener('offline', updateOnlineStatus);

  updateOnlineStatus();
}*/

// Stats
const statsClass = (window as any).Stats;
const stats = statsClass ? new statsClass() : undefined;
function animate() {
  stats.begin();
  // monitored code goes here
  stats.end();
  requestAnimationFrame(animate);
}

if (stats) {
  const panels = [0, 2]; // 0: fps, 1: ms, 2: mb
  Array.from((stats.dom as HTMLDivElement).children).forEach((child, index) => {
    (child as HTMLCanvasElement).style.display = panels.includes(index)
      ? 'inline-block'
      : 'none';
  });
  document.body.appendChild(stats.dom);
  requestAnimationFrame(animate);
}

/*
const saveExamples = async () => {
  const examplesRef = firebase.database().ref(`/examples`);
  const snapshot = await examplesRef.once('value');
  if (snapshot.exists()) {
    return;
  }
  for (let i = 0; i < examples.length; i++) {
    const ex = examples[i];
    console.log(`save ${ex.id}`);
    const ref = firebase.database().ref(`/examples/${ex.id}`);
    delete ex.id;
    await ref.set({
      ...ex,
      index: i
    });
  }
};

saveExamples()
  .then(() => {
    console.log('done');
  })
  .catch(e => {
    console.log(e);
  });
*/



// WEBPACK FOOTER //
// ./src/index.tsx